import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link } from 'wouter';
import { usePersona } from '@/contexts/persona-context';
import { usePlanSidebar } from '@/contexts/plan-sidebar-context';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Target,
  ChevronDown,
  Plus,
  X,
  User,
  MapPin,
  Music,
  Calendar,
  Lock,
  Mic2,
  Building,
  SlidersHorizontal,
  Settings,
  Filter,
  ArrowLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { QuickPersona } from '@shared/schema';

// Animation keyframes for chip appearance
const chipAnimation = "animate-in fade-in-0 zoom-in-95 duration-200";

// Format date as mm/dd/yy
function formatDateShort(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00'); // Add time to avoid timezone issues
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
}

// Filter chip component for displaying active filters
interface FilterChipProps {
  label: string;
  value: string;
  onRemove: () => void;
  icon?: React.ReactNode;  // Icon to indicate filter type
  isHardFilter?: boolean;
  expandedContent?: React.ReactNode;
  readOnly?: boolean;
}

function FilterChip({ label, value, onRemove, icon, isHardFilter = false, expandedContent, readOnly = false }: FilterChipProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className={cn("relative", chipAnimation)}>
      <div
        className={cn(
          "inline-flex flex-col rounded-lg border overflow-hidden transition-all hover:shadow-sm",
          isHardFilter
            ? "bg-purple-50 text-purple-800 border-purple-300 hover:bg-purple-100"
            : "bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100"
        )}
        onMouseEnter={() => expandedContent && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        title={value} // Show full value on hover
      >
        {/* Label row with border separator */}
        <div className={cn(
          "px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide border-b",
          isHardFilter ? "border-purple-200 bg-purple-100/50" : "border-blue-200 bg-blue-100/50"
        )}>
          {label}
        </div>
        {/* Value row */}
        <div className="flex items-center gap-1.5 px-2.5 py-1">
          <span className="text-sm font-medium max-w-[180px] truncate">{value}</span>
          {!readOnly && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="ml-auto rounded-full p-0.5 hover:bg-black/10 transition-colors flex-shrink-0"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {showTooltip && expandedContent && (
        <div className="absolute top-full left-0 mt-2 z-50 w-64 p-3 bg-white border border-gray-200 rounded-lg shadow-lg">
          {expandedContent}
        </div>
      )}
    </div>
  );
}

// Date range filter popover
interface DateFilterPopoverProps {
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
}

function clampToToday(value: string): string {
  if (!value) return value;
  const today = new Date().toISOString().split('T')[0];
  return value < today ? today : value;
}

function DateFilterPopover({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
}: DateFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const hasDateFilter = dateFrom || dateTo;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-9 gap-2",
            hasDateFilter && "bg-primary/10 text-primary border-primary"
          )}
        >
          <Calendar className="h-4 w-4" />
          <span className="hidden sm:inline">
            {hasDateFilter ? 'Date Set' : 'Dates'}
          </span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4" align="start">
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className="font-medium">Date Range</span>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1.5">From</label>
              <Input
                type="date"
                value={dateFrom}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => onDateFromChange(clampToToday(e.target.value))}
                className="h-9"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">To</label>
              <Input
                type="date"
                value={dateTo}
                min={dateFrom || new Date().toISOString().split('T')[0]}
                onChange={(e) => onDateToChange(clampToToday(e.target.value))}
                className="h-9"
              />
            </div>
          </div>
          {hasDateFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onDateFromChange('');
                onDateToChange('');
              }}
              className="w-full h-8 text-xs"
            >
              Clear Dates
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Main UnifiedFilterBar component
export interface UnifiedFilterBarProps {
  currentMethod: 'artists' | 'venues' | 'markets' | 'shows' | 'map';
  filters: {
    location: string;
    genre: string;
    dateFrom: string;
    dateTo: string;
    artistName?: string;
    venueName?: string;
    careerStage?: string[];
    venueType?: string[];
    setArtist?: string;
  };
  onFilterChange?: (key: string, value: string | string[]) => void;
  onClearFilters?: () => void;
  onAdvancedFilters?: () => void;
  isAuthenticated?: boolean;
  className?: string;
  readOnly?: boolean;
  backLink?: string;
  backLabel?: string;
}

export function UnifiedFilterBar({
  currentMethod,
  filters,
  onFilterChange,
  onClearFilters,
  onAdvancedFilters,
  isAuthenticated = false,
  className,
  readOnly = false,
  backLink,
  backLabel,
}: UnifiedFilterBarProps) {
  const [, setLocation] = useLocation();
  const { activePersona, setActivePersona, savedPersonas, clearActivePersona } = usePersona();
  const { isOpen: isPlanSidebarOpen } = usePlanSidebar();
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(112); // Default height

  // Measure header height dynamically to handle multi-row active filters
  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeight();

    // Update on resize
    window.addEventListener('resize', updateHeight);

    // Use ResizeObserver for more accurate updates when content changes
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateHeight);
      resizeObserver.disconnect();
    };
  }, []);

  // Fetch quick personas
  const { data: quickPersonasData = [] } = useQuery({
    queryKey: ['/api/quick-personas'],
    queryFn: async () => {
      const response = await fetch('/api/quick-personas');
      if (!response.ok) return [];
      return response.json();
    },
  });

  // Transform quick personas
  const quickPersonas = (quickPersonasData as QuickPersona[]).map((persona) => ({
    name: persona.name,
    icon: persona.icon,
    data: {
      profileName: persona.name,
      description: persona.description || '',
      ageRanges: persona.ageRanges || [],
      genders: persona.genders || [],
      ethnicities: persona.ethnicities || [],
      incomeLevels: persona.incomeLevels || [],
      lifestyleAttributes: persona.lifestyleAttributes || [],
      musicGenres: persona.musicGenres || [],
      targetLocations: persona.targetLocations || [],
    },
  }));

  // Navigate while preserving filters
  const handleNavigate = (path: string) => {
    const params = new URLSearchParams();
    if (filters.location) params.set('location', filters.location);
    if (filters.genre) params.set('genre', filters.genre);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.artistName) params.set('artistName', filters.artistName);
    if (filters.venueName) params.set('venueName', filters.venueName);
    if (filters.careerStage && filters.careerStage.length > 0) {
      params.set('careerStage', filters.careerStage.join(','));
    }
    if (filters.venueType && filters.venueType.length > 0) {
      params.set('venueType', filters.venueType.join(','));
    }
    if (filters.setArtist) {
      params.set('setArtist', filters.setArtist);
    }
    const queryString = params.toString();
    setLocation(`${path}${queryString ? `?${queryString}` : ''}`);
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.location ||
    filters.genre ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.artistName ||
    filters.venueName ||
    (filters.careerStage && filters.careerStage.length > 0) ||
    (filters.venueType && filters.venueType.length > 0) ||
    filters.setArtist ||
    activePersona;

  // Get active filter count for display
  const activeFilterCount = [
    filters.location,
    filters.genre,
    filters.dateFrom || filters.dateTo,
    filters.artistName,
    filters.venueName,
    filters.careerStage && filters.careerStage.length > 0,
    filters.venueType && filters.venueType.length > 0,
    filters.setArtist,
    activePersona,
  ].filter(Boolean).length;

  return (
    <>
      {/* FIXED SECTION - Browse Menu + Filter Controls + Active Filters */}
      <div
        ref={headerRef}
        className={cn(
          'fixed top-16 left-0 z-40 bg-white/90 backdrop-blur-sm',
          'transition-[right] duration-300 ease-in-out',
          isPlanSidebarOpen ? 'right-0 lg:right-96' : 'right-0',
          className
        )}
      >
        {/* Row 1: Back Button (readOnly) or Browse Navigation + Filter Controls */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          {readOnly ? (
            <Link href={backLink || '/'}>
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {backLabel || 'Back to Browse'}
              </Button>
            </Link>
          ) : (
          <div className="flex items-center justify-between">
            {/* LEFT: Browse Navigation */}
            <div className="flex items-center gap-2">
              <Button
                onClick={() => handleNavigate('/browse-artists')}
                variant={currentMethod === 'artists' ? 'default' : 'ghost'}
                size="sm"
                className="h-8"
              >
                <Mic2 className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Artists</span>
              </Button>
              <Button
                onClick={() => handleNavigate('/browse-venues')}
                variant={currentMethod === 'venues' ? 'default' : 'ghost'}
                size="sm"
                className="h-8"
              >
                <Building className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Venues</span>
              </Button>
              <Button
                onClick={() => handleNavigate('/browse-markets')}
                variant={currentMethod === 'markets' ? 'default' : 'ghost'}
                size="sm"
                className="h-8"
              >
                <MapPin className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Markets</span>
              </Button>
            </div>

            {/* RIGHT: Filter Controls */}
            <div className="flex items-center gap-2">
              {/* Persona Filter */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-8 gap-1.5",
                      activePersona && "bg-primary/10 text-primary border-primary"
                    )}
                  >
                    <Target className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {activePersona ? activePersona.profileName : 'Persona'}
                    </span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  {!isAuthenticated ? (
                    <>
                      <div className="p-4 text-center">
                        <Target className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600 mb-3">
                          Sign in to filter by audience personas
                        </p>
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => setLocation('/login')}
                        >
                          Sign In
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      {activePersona && (
                        <>
                          <DropdownMenuLabel className="flex items-center justify-between">
                            <span>Active Persona</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs text-red-600 hover:text-red-700"
                              onClick={clearActivePersona}
                            >
                              Clear
                            </Button>
                          </DropdownMenuLabel>
                          <div className="px-2 py-1.5 mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="bg-primary/10 text-primary">
                                {activePersona.profileName}
                              </Badge>
                            </div>
                            {activePersona.description && (
                              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                {activePersona.description}
                              </p>
                            )}
                          </div>
                          <DropdownMenuSeparator />
                        </>
                      )}

                      <DropdownMenuLabel>Quick Personas</DropdownMenuLabel>
                      {quickPersonas.map((template: any) => (
                        <DropdownMenuItem
                          key={template.name}
                          onClick={() => {
                            const quickPersona = {
                              ...template.data,
                              id: Math.floor(Math.random() * 1000000),
                              isQuickTemplate: true,
                            } as any;
                            setActivePersona(quickPersona);
                          }}
                        >
                          <span className="mr-2">{template.icon}</span>
                          <span>{template.name}</span>
                        </DropdownMenuItem>
                      ))}

                      {savedPersonas.length > 0 && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel>My Personas</DropdownMenuLabel>
                          {savedPersonas.map((persona) => (
                            <DropdownMenuItem
                              key={persona.id}
                              onClick={() => setActivePersona(persona)}
                            >
                              <User className="h-4 w-4 mr-2" />
                              <span>{persona.profileName}</span>
                            </DropdownMenuItem>
                          ))}
                        </>
                      )}

                      <DropdownMenuSeparator />
                      <Link href="/persona/new">
                        <DropdownMenuItem>
                          <Plus className="h-4 w-4 mr-2" />
                          <span>Create Persona</span>
                        </DropdownMenuItem>
                      </Link>
                      <Link href="/personas">
                        <DropdownMenuItem>
                          <Settings className="h-4 w-4 mr-2" />
                          <span>Manage Personas</span>
                        </DropdownMenuItem>
                      </Link>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Location Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-8 gap-1.5",
                      filters.location && "bg-primary/10 text-primary border-primary"
                    )}
                  >
                    <MapPin className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {filters.location || 'Location'}
                    </span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-4" align="end">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">Location</span>
                    </div>
                    <Input
                      placeholder="City, State (e.g., Austin, TX)"
                      value={filters.location}
                      onChange={(e) => onFilterChange?.('location', e.target.value)}
                      className="h-9"
                    />
                    {filters.location && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onFilterChange?.('location', '')}
                        className="w-full h-8 text-xs"
                      >
                        Clear Location
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Genre Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-8 gap-1.5",
                      filters.genre && "bg-primary/10 text-primary border-primary"
                    )}
                  >
                    <Music className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {filters.genre || 'Genre'}
                    </span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-4" align="end">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Music className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">Genre</span>
                    </div>
                    <Input
                      placeholder="e.g., Pop, Rock, Hip-Hop"
                      value={filters.genre}
                      onChange={(e) => onFilterChange?.('genre', e.target.value)}
                      className="h-9"
                    />
                    {filters.genre && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onFilterChange?.('genre', '')}
                        className="w-full h-8 text-xs"
                      >
                        Clear Genre
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Date Filter */}
              <DateFilterPopover
                dateFrom={filters.dateFrom}
                dateTo={filters.dateTo}
                onDateFromChange={(value) => onFilterChange?.('dateFrom', value)}
                onDateToChange={(value) => onFilterChange?.('dateTo', value)}
              />

              {/* Advanced Filters */}
              {onAdvancedFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-8 gap-1.5",
                    (filters.artistName || filters.venueName ||
                     (filters.careerStage && filters.careerStage.length > 0) ||
                     (filters.venueType && filters.venueType.length > 0)) &&
                    "bg-primary/10 text-primary border-primary"
                  )}
                  onClick={onAdvancedFilters}
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  <span className="hidden sm:inline">Advanced</span>
                </Button>
              )}

              {/* Clear All */}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onClearFilters?.();
                    if (activePersona) {
                      clearActivePersona();
                    }
                  }}
                  className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Clear</span>
                </Button>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Row 2: Active Filter Chips */}
        {/* In readOnly mode, skip Row 2 entirely when no filters are active */}
        {(readOnly ? hasActiveFilters : true) && (
        <div className={cn(
          hasActiveFilters
            ? "bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border-b border-green-200 dark:border-green-700"
            : "bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-blue-200 dark:border-blue-700"
        )}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            {hasActiveFilters ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-green-700 dark:text-green-300 font-bold mr-1 inline-flex items-center gap-1"><Filter className="h-3.5 w-3.5" />Active Filters:</span>

                {/* Persona Chip */}
                {activePersona && (
                  <FilterChip
                    label="Persona"
                    value={activePersona.profileName}
                    onRemove={clearActivePersona}
                    readOnly={readOnly}
                    expandedContent={
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">{activePersona.profileName}</h4>
                        {activePersona.description && (
                          <p className="text-xs text-gray-600">{activePersona.description}</p>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {activePersona.ageRanges?.map((age) => (
                            <Badge key={age} variant="outline" className="text-xs">
                              {age}
                            </Badge>
                          ))}
                          {activePersona.genders?.map((gender) => (
                            <Badge key={gender} variant="outline" className="text-xs">
                              {gender}
                            </Badge>
                          ))}
                          {activePersona.ethnicities?.map((ethnicity) => (
                            <Badge key={ethnicity} variant="outline" className="text-xs">
                              {ethnicity}
                            </Badge>
                          ))}
                          {activePersona.incomeLevels?.map((income) => (
                            <Badge key={income} variant="outline" className="text-xs">
                              {income}
                            </Badge>
                          ))}
                          {activePersona.lifestyleAttributes?.map((attr) => (
                            <Badge key={attr} variant="outline" className="text-xs">
                              {attr}
                            </Badge>
                          ))}
                          {activePersona.targetLocations?.map((location) => (
                            <Badge key={location} variant="outline" className="text-xs">
                              {location}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    }
                  />
                )}

                {/* DMA Chips (Hard Filters) */}
                {activePersona?.targetLocations?.map((location) => (
                  <FilterChip
                    key={location}
                    label="Market"
                    value={location}
                    isHardFilter
                    readOnly={readOnly}
                    onRemove={() => {
                      const newLocations = activePersona.targetLocations?.filter(
                        (l) => l !== location
                      );
                      if (newLocations && newLocations.length > 0) {
                        setActivePersona({
                          ...activePersona,
                          targetLocations: newLocations,
                        });
                      }
                    }}
                  />
                ))}

                {/* Location Chip */}
                {filters.location && (
                  <FilterChip
                    label="Location"
                    value={filters.location}
                    readOnly={readOnly}
                    onRemove={() => onFilterChange?.('location', '')}
                  />
                )}

                {/* Genre Chip */}
                {filters.genre && (
                  <FilterChip
                    label="Genre"
                    value={filters.genre}
                    readOnly={readOnly}
                    onRemove={() => onFilterChange?.('genre', '')}
                  />
                )}

                {/* Date Chip */}
                {(filters.dateFrom || filters.dateTo) && (
                  <FilterChip
                    label="Dates"
                    value={
                      filters.dateFrom && filters.dateTo
                        ? `${formatDateShort(filters.dateFrom)} - ${formatDateShort(filters.dateTo)}`
                        : filters.dateFrom
                        ? `From ${formatDateShort(filters.dateFrom)}`
                        : `Until ${formatDateShort(filters.dateTo)}`
                    }
                    readOnly={readOnly}
                    onRemove={() => {
                      onFilterChange?.('dateFrom', '');
                      onFilterChange?.('dateTo', '');
                    }}
                  />
                )}

                {/* Artist Name Chip */}
                {filters.artistName && (
                  <FilterChip
                    label="Artist"
                    value={filters.artistName}
                    readOnly={readOnly}
                    onRemove={() => onFilterChange?.('artistName', '')}
                  />
                )}

                {/* Venue Name Chip */}
                {filters.venueName && (
                  <FilterChip
                    label="Venue"
                    value={filters.venueName}
                    readOnly={readOnly}
                    onRemove={() => onFilterChange?.('venueName', '')}
                  />
                )}

                {/* Career Stage Chips */}
                {filters.careerStage?.map((stage) => (
                  <FilterChip
                    key={stage}
                    label="Career Stage"
                    value={stage}
                    readOnly={readOnly}
                    onRemove={() =>
                      onFilterChange?.(
                        'careerStage',
                        filters.careerStage?.filter((s) => s !== stage) || []
                      )
                    }
                  />
                ))}

                {/* Venue Type Chips */}
                {filters.venueType?.map((type) => (
                  <FilterChip
                    key={type}
                    label="Venue Type"
                    value={type}
                    readOnly={readOnly}
                    onRemove={() =>
                      onFilterChange?.(
                        'venueType',
                        filters.venueType?.filter((t) => t !== type) || []
                      )
                    }
                  />
                ))}

                {/* SET Artist Chip */}
                {filters.setArtist && (
                  <FilterChip
                    label="SET Artist"
                    value={filters.setArtist === 'yes' ? 'Yes' : 'No'}
                    readOnly={readOnly}
                    onRemove={() => onFilterChange?.('setArtist', '')}
                  />
                )}
              </div>
            ) : (
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Showing all artists with upcoming shows - Select a basic filter or a persona to find shows for your target audience
              </p>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Spacer to push content below fixed section - height adjusts dynamically */}
      <div style={{ height: headerHeight }} />

    </>
  );
}

export default UnifiedFilterBar;
